import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { TravellerWithCollectionMembershipsDTO } from '@/api/trafikportalen';
import { getTravellerStatus } from '@/helpers/status';
import { StatusIndicator } from '@/components/StatusIndicator';
import { IconSmartcard, IconSmartphoneDevice } from '@/icons';
import { useTenantID } from '@/hooks/useTenantID';
import Button from '../core/Button';

interface TravellerListItemProps {
  traveller: TravellerWithCollectionMembershipsDTO;
  allpayCredit?: number;
  cardCredit?: number;
  hideDepartment?: boolean;
}

export const TravellerListItem = ({
  traveller,
  allpayCredit,
  cardCredit,
  hideDepartment = false,
}: TravellerListItemProps) => {
  const tenantId = useTenantID();
  const { formatMessage, locale } = useIntl();
  const { traveller: travellerData, memberships } = traveller;
  const { id, first_name, last_name } = travellerData;
  const status = getTravellerStatus(traveller);

  const inactivateAt = useMemo(() => {
    return memberships.find((membership) => membership.inactivate_at)
      ?.inactivate_at;
  }, [memberships]);

  return (
    <div key={id} className="@container">
      <Button
        to={`/$tenantId/travellers/$travellerId`}
        params={{ tenantId, travellerId: id }}
        className="h-fit w-full gap-4 font-normal"
      >
        <StatusIndicator status={status} inactivateAt={inactivateAt} />
        <div className="flex min-w-0 flex-1 flex-col items-start">
          <div className="flex w-full items-center justify-between gap-2">
            {/* The pr-1 is to prevent the text from being cut off */}
            <span className="min-w-0 truncate pr-1 font-medium">
              {first_name ?? ''}
              {last_name ? ` ${last_name}` : ''}
              {!first_name && !last_name && (
                <i>
                  <FormattedMessage defaultMessage="Anonym resenär" />
                </i>
              )}
            </span>

            {!!allpayCredit && allpayCredit > 0 && (
              <div
                className="flex items-center gap-2 whitespace-nowrap"
                title={formatMessage({ defaultMessage: 'Kredit i appen' })}
              >
                <IconSmartphoneDevice
                  className="min-w-4 text-text-700"
                  aria-hidden="true"
                />
                <p>
                  {allpayCredit.toLocaleString(locale)}{' '}
                  <span className="text-text-700">
                    <FormattedMessage defaultMessage="SEK" />
                  </span>
                </p>
              </div>
            )}
          </div>
          {!hideDepartment && (
            <div className="flex w-full justify-between gap-2">
              <p className="min-w-0 flex-1 truncate text-left text-text-700">
                {memberships
                  .map((membership) => membership.department_name)
                  .join(', ')}
              </p>
              {!!cardCredit && cardCredit > 0 && (
                <div
                  className="flex items-center gap-2 whitespace-nowrap"
                  title={formatMessage({ defaultMessage: 'Kredit på kort' })}
                >
                  <IconSmartcard
                    className="min-w-4 text-text-700"
                    aria-hidden="true"
                  />
                  <p>
                    {cardCredit.toLocaleString(locale)}{' '}
                    <span className="text-text-700">
                      <FormattedMessage defaultMessage="SEK" />
                    </span>
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </Button>
    </div>
  );
};
